import { useMutation } from '@tanstack/react-query';

import { useMutationHTTPRequest } from 'api/hooks/useHTTPRequest';
import { logError } from 'lib/sentry/logError';

import { CUSTOM_STRUCTURE_CODE } from 'features/sessions/config';
import { TranscriptInsightsStructureResponse } from 'features/sessions/config/types';
import { useSessionNoteCustomStructureQueryActions } from 'features/sessions/hooks/useSessionNoteCustomStructureQueryActions';

import { showErrorToast, showSuccessToast } from 'shared/components/Toast';

export const useSessionTranscriptInsightsStructureEditMutation = () => {
  const { request } = useMutationHTTPRequest();
  const { getCustomStructure, cancelQueries, setCustomStructure } =
    useSessionNoteCustomStructureQueryActions(CUSTOM_STRUCTURE_CODE);

  return useMutation<
    TranscriptInsightsStructureResponse,
    Error,
    { customStructure: string },
    { previousData: TranscriptInsightsStructureResponse | undefined }
  >(
    async ({ customStructure }) =>
      await request({
        url: `/api/coach/transcript-insights-structure/custom/edit`,
        body: {
          structure: customStructure,
        },
      }),
    {
      onMutate: ({ customStructure }) => {
        const previousData = getCustomStructure();

        cancelQueries();

        setCustomStructure({
          insightsStructureCode: 'custom',
          structure: customStructure,
        });

        return { previousData };
      },
      onSuccess: (data) => {
        showSuccessToast('Changes saved successfully.');
        setCustomStructure(data);
      },
      onError: (error, _, context) => {
        if (context?.previousData) {
          setCustomStructure(context.previousData);
        }
        showErrorToast('Failed to save changes. Please try again.');
        logError(error);
      },
      useErrorBoundary: false,
      retry: 1,
    }
  );
};
